import { motion } from 'framer-motion'
import AtomBackground from './Atombackground'
import Reveal from './Reveal'

const milestones = [
  { year: '2012', text: 'Graduación con honores · Universidad de Buenos Aires' },
  { year: '2016', text: 'Especialización e inicio de actividad docente universitaria' },
  { year: '2019', text: 'Lanzamiento de primeros cursos online · +1.000 alumnos en año 1' },
  { year: '2024', text: 'Comunidad consolidada · 210k seguidores · 6 cursos activos' },
]

function MoleculeDecor() {
  return (
    <svg
      width="120" height="120" viewBox="0 0 120 120"
      className="absolute -top-6 -right-6 opacity-[0.07] pointer-events-none"
      fill="none"
    >
      <circle cx="60" cy="60" r="40" stroke="#C9A84C" strokeWidth="1"/>
      <circle cx="60" cy="60" r="25" stroke="#C9A84C" strokeWidth="0.5"/>
      <circle cx="60" cy="20" r="6" fill="#C9A84C"/>
      <circle cx="60" cy="100" r="6" fill="#C9A84C"/>
      <circle cx="20" cy="60" r="6" fill="#C9A84C"/>
      <circle cx="100" cy="60" r="6" fill="#C9A84C"/>
      <line x1="60" y1="20" x2="60" y2="100" stroke="#C9A84C" strokeWidth="0.5"/>
      <line x1="20" y1="60" x2="100" y2="60" stroke="#C9A84C" strokeWidth="0.5"/>
    </svg>
  )
}

export default function SobreMi() {
  return (
    <section
      id="sobre"
      className="relative overflow-hidden"
      style={{ padding: '120px 5vw', background: 'var(--bg)' }}
    >
      <AtomBackground />

      <div className="relative z-10">
        <Reveal>
          <div className="section-label">Sobre mí</div>
          <h2
            className="font-display font-black leading-[1.04] tracking-[-0.02em] mb-4"
            style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', color: 'var(--ink)' }}
          >
            Una trayectoria al servicio<br />
            de{' '}
            <em className="not-italic" style={{ color: 'var(--gold)' }}>
              tu aprendizaje
            </em>
            .
          </h2>
        </Reveal>

        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mt-16 items-center"
        >
          {/* Photo */}
          <Reveal delay={0.1}>
            <div className="relative">
              {/* Decorative corners */}
              <div
                className="absolute -top-4 -right-4 w-16 h-16 pointer-events-none"
                style={{ borderTop: '2px solid var(--gold)', borderRight: '2px solid var(--gold)' }}
              />
              <div
                className="absolute -bottom-4 -left-4 w-16 h-16 pointer-events-none"
                style={{ borderBottom: '2px solid var(--gold)', borderLeft: '2px solid var(--gold)' }}
              />
              <MoleculeDecor />

              {/* Photo placeholder */}
              <div
                className="w-full flex flex-col items-center justify-center gap-4 relative overflow-hidden"
                style={{
                  aspectRatio: '3/4',
                  background: 'var(--bg-3)',
                  border: '1px solid var(--line)',
                }}
              >
                <svg width="72" height="72" viewBox="0 0 72 72" fill="none" opacity="0.2">
                  <circle cx="36" cy="26" r="14" stroke="#C9A84C" strokeWidth="1.5"/>
                  <path d="M8 64c0-15.464 12.536-28 28-28s28 12.536 28 28" stroke="#C9A84C" strokeWidth="1.5"/>
                </svg>
                <span
                  className="font-display font-semibold text-[11px] tracking-[0.14em] uppercase"
                  style={{ color: 'var(--ink-3)' }}
                >
                  Foto profesional
                </span>
              </div>

              {/* Tag */}
              <div
                className="absolute bottom-8 -left-6 font-display font-black text-[11px] tracking-[0.1em] uppercase px-5 py-2.5"
                style={{ background: 'var(--gold)', color: '#000' }}
              >
                Médico Especialista
              </div>
            </div>
          </Reveal>

          {/* Text + timeline */}
          <Reveal delay={0.2}>
            <div>
              <p
                className="font-body font-light text-[15px] leading-[1.9] mb-5"
                style={{ color: 'var(--ink-2)' }}
              >
                Médico especialista con más de 12 años de experiencia clínica y docente.
                Creo profundamente en que el conocimiento médico de calidad debe estar
                al alcance de todos los que lo buscan con seriedad.
              </p>
              <p
                className="font-body font-light text-[15px] leading-[1.9] mb-8"
                style={{ color: 'var(--ink-2)' }}
              >
                Mi metodología combina rigor académico con claridad pedagógica —
                sin tecnicismos innecesarios, sin atajos, pero siempre directo al
                punto que importa.
              </p>

              {/* Video placeholder */}
              <motion.div
                className="w-full flex flex-col items-center justify-center gap-3 mb-10 cursor-pointer"
                style={{
                  aspectRatio: '16/9',
                  background: 'var(--bg-3)',
                  border: '1px solid var(--line)',
                }}
                whileHover={{ borderColor: 'rgba(201,168,76,0.6)' }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ border: '1.5px solid var(--gold)' }}
                  whileHover={{ scale: 1.1 }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M6 3.5l9 5.5-9 5.5V3.5z" fill="#C9A84C"/>
                  </svg>
                </motion.div>
                <span
                  className="font-display font-semibold text-[11px] tracking-[0.1em] uppercase"
                  style={{ color: 'var(--ink-3)' }}
                >
                  Video de presentación
                </span>
              </motion.div>

              {/* Timeline */}
              <div
                className="flex flex-col pl-7"
                style={{ borderLeft: '1px solid var(--line)' }}
              >
                {milestones.map((m, i) => (
                  <motion.div
                    key={m.year}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="relative flex gap-6 py-4"
                  >
                    {/* Dot */}
                    <div
                      className="absolute -left-[25px] top-[22px] w-2 h-2 rounded-full"
                      style={{ background: 'var(--gold)' }}
                    />
                    <span
                      className="font-display font-black text-[13px] shrink-0 pt-0.5"
                      style={{ color: 'var(--gold)', minWidth: '36px' }}
                    >
                      {m.year}
                    </span>
                    <span
                      className="font-body text-[13px] leading-[1.65]"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      {m.text}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}